import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { usePullToRefreshRegistry } from "./PullToRefreshContext";
import type { PullToRefreshHandler } from "./PullToRefreshContext";

// ── Tuning ─────────────────────────────────────────────────────────────────
/** Finger travel (px) is scaled by this so the pull feels weighted, not 1:1. */
const RESISTANCE = 0.5;
/** Resisted distance (px) at/after which release triggers a refresh. */
const THRESHOLD = 72;
/** Resisted distance is clamped here so the indicator never runs away. */
const MAX_PULL = 110;
/** Where the spinner rests (px from container top) while refreshing. */
const REFRESH_RESTING = 64;
/** Deadzone (raw px) before we claim the gesture, to avoid stealing taps. */
const START_DEADZONE = 6;
/** Keep the spinner visible at least this long so a fast refetch doesn't flash. */
const MIN_SPINNER_MS = 600;

type PullToRefreshHostProps = {
  /**
   * Ref to the scroll container the gesture attaches to (AppShell's <main>).
   * `| null` because React 19's `@types/react` types `useRef<T>(null)` as
   * `RefObject<T | null>`; the gesture already null-checks `scrollRef.current`.
   */
  scrollRef: RefObject<HTMLElement | null>;
};

/**
 * The single, app-wide pull-to-refresh gesture + indicator. Attaches native
 * touch listeners to the shared scroll container and invokes whatever handler
 * the active page registered via `usePullToRefresh`. Mobile-only; inert when no
 * handler is registered (so unregistered pages simply do nothing).
 *
 * Visuals use a plain CSS transform + transition rather than spring physics:
 * during the drag the indicator follows the finger 1:1 (transition off); on
 * release/reset it eases back (transition on). This is more predictable under
 * touch than a spring and keeps the moving parts minimal.
 */
export function PullToRefreshHost({ scrollRef }: PullToRefreshHostProps) {
  const isMobile = useIsMobile();
  const registry = usePullToRefreshRegistry();
  const handlerRef = registry?.handlerRef;
  const defaultHandlerRef = registry?.defaultHandlerRef;

  // `pull` drives the transform; `settling` toggles the eased transition;
  // `refreshing` shows the spinning loader. Anchor is the container's viewport
  // offset, captured at gesture start so the indicator sits at its top edge.
  const [pull, setPull] = useState(0);
  const [settling, setSettling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [anchorTop, setAnchorTop] = useState(0);

  const refreshingRef = useRef(false);
  const minTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef(0);
  const pendingPull = useRef(0);

  // rAF-throttle indicator updates so a burst of touchmove events collapses to
  // at most one setState per frame.
  const schedulePull = useCallback((distance: number) => {
    pendingPull.current = distance;
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      setPull(pendingPull.current);
    });
  }, []);

  // Takes the handler as an ARGUMENT rather than reading `handlerRef.current`
  // itself. Reading the ref inside made the compiler infer `handlerRef?.current`
  // as a dependency while the source declared `[handlerRef]`; the mismatch made
  // it bail out of optimizing the whole component
  // (`react-hooks/preserve-manual-memoization`). The only caller is the
  // `touchend` handler below, which already holds a non-null `handlerRef` and is
  // exactly where reading `.current` (latest handler at gesture time) belongs.
  // Deps are now genuinely `[]`, matching what the compiler infers.
  const runRefresh = useCallback(async (handler: PullToRefreshHandler | null) => {
    refreshingRef.current = true;
    setRefreshing(true);
    setSettling(true);
    setPull(REFRESH_RESTING);

    const startedAt = Date.now();
    try {
      if (handler) await handler();
    } catch {
      // Swallow — a failed refresh must still release the spinner. The page's
      // own error/toast handling owns surfacing the failure.
    }
    const elapsed = Date.now() - startedAt;
    const wait = Math.max(0, MIN_SPINNER_MS - elapsed);

    if (minTimerRef.current) clearTimeout(minTimerRef.current);
    minTimerRef.current = setTimeout(() => {
      minTimerRef.current = null;
      refreshingRef.current = false;
      setRefreshing(false);
      setSettling(true);
      setPull(0);
    }, wait);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isMobile || !handlerRef) return;

    // The page's own handler wins; otherwise fall back to the app-wide default
    // so pull-to-refresh works on every page, not only the ones that opted in.
    const resolveActiveHandler = (): PullToRefreshHandler | null =>
      handlerRef.current ?? defaultHandlerRef?.current ?? null;

    let startY = 0;
    let startX = 0;
    let tracking = false; // touch started at the top; still a candidate
    let pulling = false; // confirmed downward vertical pull — we own the gesture
    let current = 0; // latest resisted distance

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (e.touches.length !== 1) return;
      if (el.scrollTop > 0) return;
      if (!resolveActiveHandler()) return; // no handler at all → stay inert
      const touch = e.touches[0];
      startY = touch.clientY;
      startX = touch.clientX;
      tracking = true;
      pulling = false;
      current = 0;
      setAnchorTop(el.getBoundingClientRect().top);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!tracking || refreshingRef.current) return;
      const touch = e.touches[0];
      const dy = touch.clientY - startY;
      const dx = touch.clientX - startX;

      if (!pulling) {
        // Not yet committed — decide whether this is our gesture.
        if (dy <= 0) {
          tracking = false; // scrolling up
          return;
        }
        if (Math.abs(dx) > Math.abs(dy)) {
          tracking = false; // horizontal swipe — let it through
          return;
        }
        if (el.scrollTop > 0) {
          tracking = false; // content scrolled away from the top
          return;
        }
        if (dy < START_DEADZONE) return; // wait past the deadzone
        pulling = true;
        setSettling(false); // follow the finger 1:1 from here
      }

      // Committed pull. If the user somehow scrolled the content, bail out.
      if (el.scrollTop > 0) {
        tracking = false;
        pulling = false;
        setSettling(true);
        schedulePull(0);
        return;
      }

      // Stop native scroll/overscroll so only our indicator moves.
      e.preventDefault();
      current = Math.max(0, Math.min(dy * RESISTANCE, MAX_PULL));
      schedulePull(current);
    };

    const onTouchEnd = () => {
      if (!tracking) return;
      const wasPulling = pulling;
      tracking = false;
      pulling = false;
      if (wasPulling && current >= THRESHOLD) {
        // Read the latest handler at gesture time — page handler, else default.
        void runRefresh(resolveActiveHandler());
      } else if (wasPulling) {
        setSettling(true);
        schedulePull(0);
      }
    };

    // touchmove must be non-passive so we can preventDefault the native pull.
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isMobile, scrollRef, handlerRef, defaultHandlerRef, runRefresh, schedulePull]);

  // Cleanup timers / frames on unmount.
  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (minTimerRef.current) clearTimeout(minTimerRef.current);
    },
    [],
  );

  if (!isMobile) return null;

  const visible = pull > 0 || refreshing;
  const progress = Math.min(pull / THRESHOLD, 1);
  // Slide the badge down from just above the container's top edge.
  const badgeSize = 32;
  const translateY = anchorTop + pull - badgeSize - 8;

  return (
    <div
      aria-hidden={!refreshing}
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center"
      style={{
        transform: `translateY(${translateY}px)`,
        opacity: visible ? 1 : 0,
        transition: settling
          ? "transform 220ms ease-out, opacity 220ms ease-out"
          : "opacity 120ms ease-out",
      }}
    >
      <div
        role="status"
        aria-live="polite"
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface,white)] shadow-md"
        style={{
          transform: refreshing ? "none" : `rotate(${progress * 270}deg)`,
        }}
      >
        <span
          className={`block h-4 w-4 rounded-full border-2 border-[color:var(--border-light)] border-t-[color:var(--accent-600)] ${
            refreshing ? "animate-spin" : ""
          }`}
          style={refreshing ? undefined : { opacity: 0.4 + progress * 0.6 }}
        />
      </div>
    </div>
  );
}
